import React, { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import { supabase } from '../supabaseClient'
import { useProfile } from '../contexts/ProfileContext'
import Header from '../components/Header'

export default function Profile() {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        birthday: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'Philippines',
        profilePictureUrl: ''
    })
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [currentUser, setCurrentUser] = useState(null)
    const [userType, setUserType] = useState(null)
    const [role, setRole] = useState('')
    const [isProfileComplete, setIsProfileComplete] = useState(false)
    const [profilePicturePreview, setProfilePicturePreview] = useState(null)
    const [uploadingPicture, setUploadingPicture] = useState(false)
    const [editFormData, setEditFormData] = useState({})
    const [editProfilePicture, setEditProfilePicture] = useState(null)
    const [editProfilePicturePreview, setEditProfilePicturePreview] = useState(null)
    const [originalFileName, setOriginalFileName] = useState(null)
    const { updateProfilePicture, updateAdminInfo } = useProfile()

    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .swal2-confirm {
                background-color: #FF6F3C !important;
                border: none !important;
            }
            .swal2-confirm:hover {
                background-color: #8B5A3C !important;
            }
            .swal2-cancel:hover {
                background-color: #5a5a5a !important;
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);
    
    useEffect(() => {
        fetchUserProfile()
    }, [])

    useEffect(() => {
        checkProfileCompletion()
    }, [formData, role])

    const checkProfileCompletion = () => {
        const requiredFields = ['firstName', 'lastName', 'phoneNumber', 'address']
        
        if (role === 'Super Admin') {
            requiredFields.push('city', 'state', 'zipCode')
        }
        
        const isComplete = requiredFields.every(field => {
            const value = formData[field]
            return value && value.trim() !== '' })
        
        setIsProfileComplete(isComplete)
    }

    const fetchUserProfile = async () => {
        try {
            setLoading(true)
            const userEmail = localStorage.getItem('userEmail') || sessionStorage.getItem('userEmail')
            const userType = localStorage.getItem('userType') || sessionStorage.getItem('userType')

            if (!userEmail || !userType) {
                console.error('No userEmail or userType found in storage')
                setLoading(false)
                return
            }

            setUserType(userType)

            if (userType === 'admin') {
                const { data: adminData, error: adminError } = await supabase
                    .from('admin_accounts')
                    .select('*')
                    .eq('email', userEmail)
                    .single()

                if (adminError) {
                    console.error('Admin account error:', adminError)
                    throw adminError
                }

                console.log('Admin data fetched:', adminData)
                setCurrentUser(adminData)
                setRole('Super Admin')

                const { data: profileData, error: profileError } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('user_id', adminData.id)
                    .eq('user_type', 'admin')
                    .maybeSingle()

                if (profileError) {
                    console.error('Profile fetch error:', profileError)
                }

                console.log('Profile data fetched:', profileData)

                if (profileData) {
                    const profileFormData = {
                        firstName: profileData.first_name || '',
                        lastName: profileData.last_name || '',
                        email: adminData.email || '',
                        phoneNumber: profileData.phone_number || '',
                        birthday: profileData.birthday || '',
                        address: profileData.address || '',
                        city: profileData.city || '',
                        state: profileData.state_province || '',
                        zipCode: profileData.zip_code || '',
                        country: profileData.country || 'Philippines',
                        profilePictureUrl: profileData.profile_picture_url || ''
                    }
                    console.log('Setting form data:', profileFormData)
                    setFormData(profileFormData)
                    setProfilePicturePreview(profileData.profile_picture_url || null)

                    if (profileData.profile_picture_url) {
                        const urlParts = profileData.profile_picture_url.split('/')
                        const fileName = urlParts[urlParts.length - 1]
                        setOriginalFileName(fileName)
                    }
                } else {
                    console.log('No profile found, using initial data with email from admin_accounts')
                    const initialData = {
                        firstName: '',
                        lastName: '',
                        email: adminData.email || '',
                        phoneNumber: '',
                        birthday: '',
                        address: '',
                        city: '',
                        state: '',
                        zipCode: '',
                        country: 'Philippines',
                        profilePictureUrl: ''
                    }
                    setFormData(initialData)
                }

            } else if (userType === 'staff') {
                const { data: staffData, error: staffError } = await supabase
                    .from('staff')
                    .select('*')
                    .or(`email.eq.${userEmail},company_email.eq.${userEmail}`)
                    .single()

                if (staffError) {
                    console.error('Staff account error:', staffError)
                    throw staffError
                }

                console.log('Staff data fetched:', staffData)
                setCurrentUser(staffData)
                setRole(staffData.role || 'Staff')

                const { data: profileData, error: profileError } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('user_id', staffData.id)
                    .eq('user_type', 'staff')
                    .maybeSingle()

                if (profileError) {
                    console.error('Profile fetch error:', profileError)
                }

                console.log('Profile data fetched:', profileData)

                if (profileData) {
                    const profileFormData = {
                        // Name/phone now come from the branch's Branch Owner (staffData),
                        // not the personal `profiles` table — a staff member can't edit these.
                        firstName: staffData.first_name || '',
                        lastName: staffData.last_name || '',
                        email: staffData.email || '',
                        phoneNumber: staffData.phone_number || '',
                        birthday: profileData.birthday || '',
                        address: profileData.address || '',
                        city: profileData.city || '',
                        state: profileData.state_province || '',
                        zipCode: profileData.zip_code || '',
                        country: profileData.country || 'Philippines',
                        profilePictureUrl: profileData.profile_picture_url || ''
                    }
                    console.log('Setting form data:', profileFormData)
                    setFormData(profileFormData)
                    setProfilePicturePreview(profileData.profile_picture_url || null)
                } else {
                    console.log('No profile found, using initial data from staff table')
                    const initialData = {
                        firstName: staffData.first_name || '',
                        lastName: staffData.last_name || '',
                        email: staffData.email || '',
                        phoneNumber: staffData.phone_number || '',
                        birthday: '',
                        address: '',
                        city: '',
                        state: '',
                        zipCode: '',
                        country: 'Philippines',
                        profilePictureUrl: ''
                    }
                    setFormData(initialData)
                }
            }
        } catch (err) {
            console.error('Error fetching profile: ', err)
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load profile data. Please try again.'
            })
        } finally {
            setLoading(false)
        }
    }

    const calculateFileHash = async (file) => {
        const arrayBuffer = await file.arrayBuffer()
        const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
        return hashHex
    }

    const getImageHash = async (url) => {
        try {
            const response = await fetch(url)
            const blob = await response.blob()
            const arrayBuffer = await blob.arrayBuffer()
            const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer)
            const hashArray = Array.from(new Uint8Array(hashBuffer))
            const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
            return hashHex
        } catch (err) {
            console.error('Error getting image hash: ', err)
            return null
        }
    }

    const getChangedFields = () => {
        const changed = {}
        // firstName/lastName/phoneNumber removed for staff — those are
        // branch-owned fields now and aren't submitted in change requests.
        const trackFields = role === 'Super Admin'
            ? ['firstName', 'lastName', 'phoneNumber', 'birthday', 'address', 'city', 'state', 'zipCode', 'country', 'profilePictureUrl']
            : ['birthday', 'address', 'city', 'state', 'zipCode', 'country', 'profilePictureUrl']
        trackFields.forEach(field => {
            const oldVal = (formData[field] || '').toString().trim()
            const newVal = (editFormData[field] || '').toString().trim()
            if(oldVal !== newVal) changed[field] = newVal
        })
        if (editProfilePicture) changed._newPictureUploaded = true
        return changed
    }

    const validateProfile = (data = formData) => {
        const errors = []

        // First/Last Name and Phone Number are only validated (and editable)
        // for Super Admin. For staff, these mirror the Branch Owner and are
        // managed in Branch Management, not here.
        if (role === 'Super Admin') {
            if (!data.firstName.trim()) {
                errors.push('First Name is required')
            } else if (data.firstName.length > 30) {
                errors.push('First name must not exceed 30 characters')
            } else if (!/^[a-zA-Z\s]+$/.test(data.firstName)) {
                errors.push('First name must contain only letters')
            }

            if (!data.lastName.trim()) {
                errors.push('Last Name is required')
            } else if (data.lastName.length > 30) {
                errors.push('Last name must not exceed 30 characters')
            } else if (!/^[a-zA-Z\s]+$/.test(data.lastName)) {
                errors.push('Last name must contain only letters')
            }

            if (!data.phoneNumber.trim()) {
                errors.push('Phone Number is required')
            } else {
                const cleanPhone = data.phoneNumber.replace(/[\s-]/g, '')
                if (!/^(09|\+639)\d{9}$/.test(cleanPhone)) {
                    errors.push('Please enter a valid Philippine phone number (e.g., 09123456789 or +639123456789)')
                }
            }
        }

        if (data.birthday) {
            const birthDate = new Date(data.birthday)
            const today = new Date()
            const age = today.getFullYear() - birthDate.getFullYear()
            const monthDiff = today.getMonth() - birthDate.getMonth()
            const dayDiff = today.getDate() - birthDate.getDate()
            
            const actualAge = (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) ? age - 1 : age
            
            if (actualAge < 18) {
                errors.push('You must be at least 18 years old')
            }
            
            if (birthDate > today) {
                errors.push('Birthday cannot be in the future')
            }
        }

        if (role === 'Super Admin') {
            if (!data.address.trim()) {
                errors.push('Address is required for Super Admin')
            }
            if (!data.city.trim()) {
                errors.push('City is required for Super Admin')
            }
            if (!data.state.trim()) {
                errors.push('State/Province is required for Super Admin')
            }
            if (!data.zipCode.trim()) {
                errors.push('Zip Code is required for Super Admin')
            }
        } else {
            if (!data.address.trim()) {
                errors.push('Address is required')
            }
        }
        
        return errors
    }

    const handleEditProfilePictureChange = (e) => {
        const file = e.target.files[0]
        console.log('Filer selected: ', file)
        
        if (file) {
            if (!file.type.startsWith('image/')) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File Type',
                    text: 'Please select an image file (JPG, PNG, GIF, etc.)',
                    confirmButtonColor: '#FF6F3C'
                })
                return
            }

            if (file.size > 5 * 1024 * 1024) {
                Swal.fire({
                    icon: 'error',
                    title: 'File Too Large',
                    text: 'Please select an image smaller than 5MB',
                    confirmButtonColor: '#FF6F3C'
                })
                return
            }

            setEditProfilePicture(file)
            
            const reader = new FileReader()
            reader.onloadend = () => {
                setEditProfilePicturePreview(reader.result)
            }
            reader.readAsDataURL(file)
        }
    }

    const uploadProfilePicture = async (userId, pictureFile) => {
        if (!pictureFile) return editFormData.profilePictureUrl

        try {
            setUploadingPicture(true)

            if (editFormData.profilePictureUrl) {
                const newFileHash = await calculateFileHash(pictureFile)
                const existingFileHash = await getImageHash(editFormData.profilePictureUrl)

                if (newFileHash === existingFileHash) {
                    return editFormData.profilePictureUrl
                }
            }

            const fileExt = pictureFile.name.split('.').pop()
            const fileName = `${userId}_${Date.now()}.${fileExt}`
            const filePath = `${fileName}` 

            console.log('Attempting upload with path:', filePath)

            if (editProfilePicturePreview === formData.profilePictureUrl) {
                console.log('Using existing profile picture URL')
                return formData.profilePictureUrl
            }

            const { data, error } = await supabase.storage
                .from('profile-pictures')
                .upload(filePath, pictureFile, {
                    cacheControl: '3600',
                    upsert: true
                })

            console.log('Upload result:', { data, error })

            if (error) throw error

            const { data: urlData } = supabase.storage
                .from('profile-pictures')
                .getPublicUrl(filePath)

            return urlData.publicUrl

        } catch (error) {
            console.error('Upload error:', error)
            throw error
        } finally {
            setUploadingPicture(false)
        }
    }

    const handleRemoveEditProfilePicture = async () => {
        const result = await Swal.fire({
            title: 'Remove Profile Picture?',
            text: 'Are you sure you want to remove your profile picture?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#FF6F3C',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Yes, remove it',
            cancelButtonText: 'Cancel'
        })

        if (!result.isConfirmed) return

        setEditProfilePicture(null)
        setEditProfilePicturePreview(null)
        setEditFormData(prev => ({ ...prev, profilePictureUrl: '' }))
    }

    const handleEditInputChange = (e) => {
        const { name, value } = e.target
        
        let formattedValue = value
        
        if (name === 'firstName' || name === 'lastName') {
            formattedValue = value.replace(/\b\w/g, l => l.toUpperCase()).replace(/[^a-zA-Z\s]/g, '')
        } else if (name === 'phoneNumber') {
            formattedValue = value.replace(/[^0-9+]/g, '')
        }
        
        setEditFormData(prev => ({
            ...prev,
            [name]: formattedValue
        }))
    }

    const handleOpenModal = () => {
        setEditFormData({ ...formData })
        setEditProfilePicturePreview(formData.profilePictureUrl || null)
        setEditProfilePicture(null)
        setIsModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setEditFormData({})
        setEditProfilePicture(null)
        setEditProfilePicturePreview(null)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        const errors = validateProfile(editFormData)
    
        if (errors.length > 0) {
            await Swal.fire({
                icon: 'warning',
                title: 'Incomplete Profile',
                html: `
                    <div style="text-align: center;">
                        <p style="margin-bottom: 10px;">Please complete the following required fields:</p>
                        <ul style="list-style-position: inside; padding-left: 0;">
                            ${errors.map(error => `<li>${error}</li>`).join('')}
                        </ul>
                    </div>
                `,
                confirmButtonColor: '#FF6F3C',
            })
            return
        }

        const changedFields = getChangedFields()
        const hasChanges = Object.keys(changedFields).filter(k => k !== '_newPictureUploaded').length > 0 || editProfilePicture

        if (!hasChanges) {
            await Swal.fire({
                icon: "info",
                title: "No Changes",
                text: "You haven't made any changes.",
                confirmButtonColor: '#FF6F3C'
            })
            return
        }

        // Staff and Super Admin both save directly now — the approval
        // workflow is no longer needed since staff identity (name/phone)
        // is branch-owned and everything left here is low-stakes personal
        // info (address, birthday, photo).
        await saveProfileDirectly()
    }

    const saveProfileDirectly = async () => {
        const result = await Swal.fire({
            title: 'Save Profile?', text: 'Save these changes?', icon: 'question',
            showCancelButton: true, confirmButtonColor: '#FF6F3C', cancelButtonColor: '#6B7280',
            confirmButtonText: 'Yes, save it!'
        })
        if (!result.isConfirmed) return
 
        try {
            setSaving(true)
            const userId = currentUser?.id
 
            let profilePictureUrl = editFormData.profilePictureUrl
            if (editProfilePicture instanceof File) {
                try {
                    profilePictureUrl = await uploadProfilePicture(userId, editProfilePicture)
                } catch {
                    await Swal.fire({ icon: 'error', title: 'Upload Failed', text: 'Failed to upload picture.', confirmButtonColor: '#FF6F3C' })
                    return
                }
            }
 
            const required = role === 'Super Admin'
                ? ['firstName', 'lastName', 'phoneNumber', 'address', 'city', 'state', 'zipCode']
                : ['address']
            const isComplete = required.every(f => editFormData[f]?.trim())
            const filledCount = required.filter(f => editFormData[f]?.trim()).length
                + (editFormData.birthday ? 1 : 0) + (editFormData.country ? 1 : 0)
            const completionPercentage = Math.round((filledCount / (required.length + 2)) * 100)
 
            const profileData = {
                user_id: userId, user_type: userType,
                first_name: editFormData.firstName,
                last_name: editFormData.lastName,
                phone_number: editFormData.phoneNumber,
                birthday: editFormData.birthday || null,
                address: editFormData.address,
                city: editFormData.city,
                state_province: editFormData.state,
                zip_code: editFormData.zipCode,
                country: editFormData.country,
                profile_picture_url: profilePictureUrl,
                is_profile_complete: isComplete,
                profile_completion_percentage: completionPercentage,
                updated_at: new Date().toISOString()
            }
 
            const { data: existing } = await supabase.from('profiles').select('id')
                .eq('user_id', userId).eq('user_type', userType).single()
 
            if (existing) {
                const { error } = await supabase.from('profiles').update(profileData)
                    .eq('user_id', userId).eq('user_type', userType)
                if (error) throw error
            } else {
                const { error } = await supabase.from('profiles').insert([profileData])
                if (error) throw error
            }
 
            await Swal.fire({ icon: 'success', title: 'Saved!', text: 'Profile updated successfully.', confirmButtonColor: '#FF6F3C' })
            updateProfilePicture(profilePictureUrl)
            updateAdminInfo(`${editFormData.firstName} ${editFormData.lastName}`, role)
            handleCloseModal()
            fetchUserProfile()
        } catch (err) {
            console.error('Error saving:', err)
            await Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save profile.', confirmButtonColor: '#FF6F3C' })
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex-1 overflow-y-auto bg-gray-50 relative">
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-50">
                    <div className='text-center'>
                         <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B5A3C] mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading profile...</p>
                    </div>
                </div>
            </div>
        )
    }

     return (
        <div className="flex-1 overflow-y-auto bg-gray-50">
            <Header title="Profile Management" subtitle="Manage your account details and personal information" onGoToProfile={handleOpenModal} />
 
            <div className="p-4 sm:p-8">
                <div className="max-w-full">
 
                    {/* ── Profile header card ── */}
                    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex items-center gap-4 min-w-0">
                                <div className="w-20 h-20 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden shrink-0">
                                    {profilePicturePreview
                                        ? <img src={profilePicturePreview} alt="Profile" className="w-full h-full object-cover"/>
                                        : <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                                            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                                        </svg>
                                    }
                                </div>
                                <div className="min-w-0">
                                    <h2
                                        className="text-lg sm:text-2xl font-bold text-gray-900 break-words"
                                        style={{ hyphens: 'auto', overflowWrap: 'break-word' }}
                                        lang="en"
                                    >
                                        {formData.firstName && formData.lastName
                                            ? `${formData.firstName} ${formData.lastName}`
                                            : 'Complete Your Profile'}
                                    </h2>
                                    <p className="text-gray-600">{role}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleOpenModal}
                                    className="px-4 py-2 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-[#ff4c0b] transition-colors flex items-center gap-2"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                    </svg>
                                    Edit Profile
                                </button>
                            </div>
                        </div>
                    </div>
 
                    {/* ── Alerts ── */}
                    {!isProfileComplete && role === 'Super Admin' && (
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg">
                            <div className="flex items-start">
                                <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                                </svg>
                                <p className="text-sm text-blue-700 font-medium">As a Super Admin, please complete all required fields including full address information.</p>
                            </div>
                        </div>
                    )}
 
                    {!isProfileComplete && userType === 'staff' && (
                        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6 rounded-r-lg">
                            <div className="flex items-start">
                                <svg className="w-5 h-5 text-amber-500 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                                </svg>
                                <p className="text-sm text-amber-700 font-medium">Please ensure your contact information and address are up to date.</p>
                            </div>
                        </div>
                    )}
 
                    {/* ── Profile display fields ── */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                                <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">{formData.firstName || '-'}</div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                                <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">{formData.lastName || '-'}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                                <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 break-words">{formData.email || '-'}</div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                                <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">{formData.phoneNumber || '-'}</div>
                            </div>
                        </div>
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Birthday</label>
                            <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">
                                {formData.birthday ? new Date(formData.birthday).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '-'}
                            </div>
                        </div>
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                            <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">{formData.address || '-'}</div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                                <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">{formData.city || '-'}</div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">State / Province</label>
                                <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">{formData.state || '-'}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Zip Code</label>
                                <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">{formData.zipCode || '-'}</div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                                <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">{formData.country || '-'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
 
            {/* ── Edit / Request Changes Modal ── */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
                        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shrink-0">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Edit Profile</h2>
                            </div>
                            <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>
 
                        <form onSubmit={handleSubmit} className= "p-6 overflow-y-auto flex-1">
                            {/* Profile Picture */}
                            <div className="mb-6 pb-6 border-b border-gray-100 flex items-center gap-5">
                                <div className="relative shrink-0">
                                    <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden ring-2 ring-gray-100">
                                        {editProfilePicturePreview
                                            ? <img src={editProfilePicturePreview} alt="Profile" className="w-full h-full object-cover"/>
                                            : <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                                                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                                            </svg>
                                        }
                                    </div>
                                    <label htmlFor="edit-profile-picture-upload" className="absolute -bottom-1 -right-1 cursor-pointer">
                                        <div className="w-7 h-7 bg-[#FF6F3C] rounded-full flex items-center justify-center hover:bg-[#e05a2b] transition-colors shadow-md">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                                <circle cx="12" cy="13" r="4"/>
                                            </svg>
                                        </div>
                                    </label>
                                    <input id="edit-profile-picture-upload" type="file" accept="image/*" onChange={handleEditProfilePictureChange} className="hidden"/>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-800 mb-0.5">Profile Picture</p>
                                    <p className="text-xs text-gray-500 mb-2">JPG, PNG or GIF · Max 5 MB</p>
                                    {editProfilePicturePreview
                                        ? <button type="button" onClick={handleRemoveEditProfilePicture} className="text-xs text-red-500 hover:text-red-700 font-medium">Remove picture</button>
                                        : <label htmlFor="edit-profile-picture-upload" className="text-xs text-[#FF6F3C] hover:text-[#e05a2b] font-medium cursor-pointer">Upload photo</label>
                                    }
                                </div>
                            </div>
 
                            {role === 'Super Admin' ? (
                                <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">First Name <span className="text-red-500">*</span></label>
                                            <input type="text" name="firstName" value={editFormData.firstName || ''} onChange={handleEditInputChange} required maxLength={30}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent" placeholder="e.g. Juan"/>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Last Name <span className="text-red-500">*</span></label>
                                            <input type="text" name="lastName" value={editFormData.lastName || ''} onChange={handleEditInputChange} required maxLength={30}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent" placeholder="e.g. Dela Cruz"/>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                                            <input type="email" name="email" value={editFormData.email || ''} disabled
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-700"/>
                                            <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number <span className="text-red-500">*</span></label>
                                            <input type="tel" name="phoneNumber" value={editFormData.phoneNumber || ''} onChange={handleEditInputChange} required maxLength={13}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent" placeholder=" e.g. 09XXXXXXXXX"/>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm font-medium text-gray-700 mb-1">{editFormData.firstName} {editFormData.lastName}</p>
                                    <p className="text-xs text-gray-500">{editFormData.email} · {editFormData.phoneNumber || 'No phone on file'}</p>
                                    <p className="text-xs text-gray-400 mt-2">This account's name, email, and phone number come from the branch's Branch Owner details and can't be edited here. Contact your Super Admin to update them in Branch Management.</p>
                                </div>
                            )}
 
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Birthday</label>
                                <input type="date" name="birthday" value={editFormData.birthday || ''} onChange={handleEditInputChange}
                                    max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().split('T')[0] })()}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent"/>
                            </div>
 
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Address <span className="text-red-500">*</span></label>
                                <input type="text" name="address" value={editFormData.address || ''} onChange={handleEditInputChange} required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent" placeholder="Street address, building, floor, etc."/>
                            </div>
 
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">City {role === 'Super Admin' && <span className="text-red-500">*</span>}</label>
                                    <input type="text" name="city" value={editFormData.city || ''} onChange={handleEditInputChange} required={role === 'Super Admin'}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent" placeholder="e.g. Manila"/>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">State / Province {role === 'Super Admin' && <span className="text-red-500">*</span>}</label>
                                    <input type="text" name="state" value={editFormData.state || ''} onChange={handleEditInputChange} required={role === 'Super Admin'}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent" placeholder="e.g. Metro Manila"/>
                                </div>
                            </div>
 
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Zip Code {role === 'Super Admin' && <span className="text-red-500">*</span>}</label>
                                    <input type="text" name="zipCode" value={editFormData.zipCode || ''} onChange={handleEditInputChange} required={role === 'Super Admin'} maxLength={4}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent" placeholder="e.g. 1000"/>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                                    <input type="text" name="country" value={editFormData.country || ''} onChange={handleEditInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] focus:border-transparent"/>
                                </div>
                            </div>
 
                            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-gray-200">
                                <button type="button" onClick={handleCloseModal}
                                    className="w-full sm:w-auto px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors">
                                    Cancel
                                </button>
                                <button type="submit" disabled={saving || uploadingPicture}
                                    className="w-full sm:w-auto justify-center px-6 py-2 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-[#ff4c0b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                                    {(saving || uploadingPicture) && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"/>}
                                    {uploadingPicture ? 'Uploading...' : saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}
 